﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  SUPPORTED_LOCALES,
  type Locale,
  type ShopHeroStyle,
} from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ImageUploader } from "@/components/image-uploader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Create ve edit için ortak form şeması.
 * - Slug create'te opsiyonel (auto-generate), edit'te form göstermez.
 * - HeroStyle edit-only — create'te backend default "Profile" uygular, organizer
 *   sonra Tema Ayarları kartından değiştirir.
 * - LogoUrl/CoverImageUrl sadece edit'te.
 */
const baseSchema = {
  name: z.string().min(2, "İsim en az 2 karakter olmalı.").max(200),
  description: z.string().max(1000).optional().or(z.literal("")),
  defaultLocale: z.enum(SUPPORTED_LOCALES),
  contactEmail: z.string().email("Geçerli bir email gir.").optional().or(z.literal("")),
  contactPhone: z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, "E.164 formatında olmalı (örn: +905551234567).")
    .optional()
    .or(z.literal("")),
};

const createSchema = z.object({
  ...baseSchema,
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Sadece küçük harf, rakam ve tire.")
    .min(3)
    .max(50)
    .optional()
    .or(z.literal("")),
});

const editSchema = z.object({
  ...baseSchema,
  logoUrl: z.string().url("Geçerli bir URL gir.").optional().or(z.literal("")),
  coverImageUrl: z.string().url("Geçerli bir URL gir.").optional().or(z.literal("")),
  heroStyle: z.enum(["Profile", "Showcase"]),
});

export type CreateShopFormValues = z.infer<typeof createSchema>;
export type EditShopFormValues = z.infer<typeof editSchema>;

type ShopFormProps =
  | {
      mode: "create";
      defaultValues?: Partial<CreateShopFormValues>;
      onSubmit: (values: CreateShopFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    }
  | {
      mode: "edit";
      /** Edit modda zorunlu — logo/cover upload ownership check'i için. */
      shopId: string;
      defaultValues?: Partial<EditShopFormValues>;
      onSubmit: (values: EditShopFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    };

export function ShopForm(props: ShopFormProps) {
  if (props.mode === "create") {
    return <CreateFormImpl {...props} />;
  }
  return <EditFormImpl {...props} />;
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

type CreateFormProps = Extract<ShopFormProps, { mode: "create" }>;

function CreateFormImpl({ defaultValues, onSubmit, isSubmitting, submitLabel }: CreateFormProps) {
  const t = useT("shops.form");
  const form = useForm<CreateShopFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: "",
      description: "",
      slug: "",
      defaultLocale: "tr",
      contactEmail: "",
      contactPhone: "",
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <Section title="Temel Bilgi">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("nameLabel")} *</FormLabel>
                <FormControl>
                  <Input placeholder="Örn: Ankara Sahne" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("slugLabel")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("slugHint")} {...field} />
                </FormControl>
                <FormDescription>
                  ticketnjoy.com/<span className="font-mono">{field.value || "ankara-sahne"}</span>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("descriptionLabel")}</FormLabel>
                <FormControl>
                  <Textarea placeholder={t("descriptionPlaceholder")} rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultLocale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("defaultLocaleLabel")} *</FormLabel>
                <FormControl>
                  <LocaleSelect value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormDescription>{t("defaultLocaleHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <Section title="İletişim">
          <ContactFields form={form} />
        </Section>

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? t("submitCreate")} />
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

type EditFormProps = Extract<ShopFormProps, { mode: "edit" }>;

function EditFormImpl({ shopId, defaultValues, onSubmit, isSubmitting, submitLabel }: EditFormProps) {
  const t = useT("shops.form");
  const form = useForm<EditShopFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: "",
      description: "",
      defaultLocale: "tr",
      logoUrl: "",
      coverImageUrl: "",
      contactEmail: "",
      contactPhone: "",
      heroStyle: "Profile",
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <Section title="Temel Bilgi">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("nameLabel")} *</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("descriptionLabel")}</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultLocale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("defaultLocaleLabel")} *</FormLabel>
                <FormControl>
                  <LocaleSelect value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormDescription>{t("defaultLocaleHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <Section title="Görsel">
          {/* Logo kompakt (64×64 avatar) + kapak geniş; mobil stack, sm+ yanyana. */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <FormField
              control={form.control}
              name="logoUrl"
              render={({ field }) => (
                <FormItem className="sm:shrink-0">
                  <FormLabel>{t("logoUrlLabel")}</FormLabel>
                  <FormControl>
                    <ImageUploader
                      kind="ShopLogo"
                      shopId={shopId}
                      aspect="square"
                      actionsPosition="beside"
                      value={typeof field.value === "string" ? field.value : ""}
                      onChange={(url) => field.onChange(url ?? "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coverImageUrl"
              render={({ field }) => (
                <FormItem className="sm:flex-1">
                  <FormLabel>{t("coverImageUrlLabel")}</FormLabel>
                  <FormControl>
                    <ImageUploader
                      kind="ShopCover"
                      shopId={shopId}
                      aspect="cover"
                      value={typeof field.value === "string" ? field.value : ""}
                      onChange={(url) => field.onChange(url ?? "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Section>

        <Section title="İletişim">
          <ContactFields form={form} />
        </Section>

        <Section
          title="Tema Ayarları"
          description="Storefront üst bandının görünümü. İlerde renk/font seçenekleri de buraya gelecek."
        >
          <FormField
            control={form.control}
            name="heroStyle"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">Hero Stili</FormLabel>
                <FormControl>
                  <HeroStyleRadio value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? t("submitUpdate")} />
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function ContactFields({ form }: { form: any }) {
  const t = useT("shops.form");
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField
        control={form.control}
        name="contactPhone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("contactPhoneLabel")}</FormLabel>
            <FormControl>
              <Input placeholder="+905551234567" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contactEmail"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("contactEmailLabel")}</FormLabel>
            <FormControl>
              <Input type="email" placeholder="mağazan@ornek.com" {...field} />
            </FormControl>
            <FormDescription>{t("contactEmailHint")}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function SubmitRow({ isSubmitting, label }: { isSubmitting?: boolean; label: string }) {
  const tCommon = useT("common");
  return (
    <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
      {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
      {isSubmitting ? tCommon("saving") : label}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Hero style radio cards
// ---------------------------------------------------------------------------

type HeroStyleOption = {
  value: ShopHeroStyle;
  title: string;
  description: string;
  preview: React.ReactNode;
};

const HERO_STYLE_OPTIONS: HeroStyleOption[] = [
  {
    value: "Profile",
    title: "Profil",
    description: "Kapak + yuvarlak logo + isim. Marka kimliği ön planda.",
    preview: <ProfilePreview />,
  },
  {
    value: "Showcase",
    title: "Vitrin",
    description: "Büyük kapak üstünde beyaz isim. Görsel ön planda.",
    preview: <ShowcasePreview />,
  },
];

function HeroStyleRadio({
  value,
  onChange,
}: {
  value: ShopHeroStyle;
  onChange: (v: ShopHeroStyle) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Hero stili"
      className="grid gap-3 sm:grid-cols-2"
    >
      {HERO_STYLE_OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "group relative flex flex-col gap-3 rounded-lg border p-3 text-left transition-colors",
              "hover:border-foreground/30",
              selected
                ? "border-primary ring-2 ring-primary/20"
                : "border-input",
            )}
          >
            {selected ? (
              <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </span>
            ) : null}
            {option.preview}
            <div>
              <div className="text-sm font-medium">{option.title}</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {option.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Profil varyantı için mini wireframe: kapak + sol altta taşan avatar + isim satırı. */
function ProfilePreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-muted">
      <div className="h-14 w-full bg-gradient-to-r from-slate-300 to-slate-400" />
      <div className="flex items-end gap-2 px-3 pb-2">
        <div className="-mt-3 h-8 w-8 shrink-0 rounded-full border-2 border-background bg-slate-500" />
        <div className="flex-1 py-1">
          <div className="h-1.5 w-20 rounded bg-foreground/70" />
          <div className="mt-1 h-1 w-28 rounded bg-foreground/30" />
        </div>
      </div>
    </div>
  );
}

/** Vitrin varyantı için mini wireframe: yüksek kapak + dip gradient + beyaz isim satırı. */
function ShowcasePreview() {
  return (
    <div className="relative overflow-hidden rounded-md border bg-muted">
      <div className="h-20 w-full bg-gradient-to-br from-slate-600 to-slate-800" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/70 to-transparent"
      />
      <div className="absolute bottom-2 left-3 right-3">
        <div className="h-1.5 w-24 rounded bg-white/95" />
        <div className="mt-1 h-1 w-32 rounded bg-white/60" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selects
// ---------------------------------------------------------------------------
// Native <select> geçici — shadcn Select eklenince migrate edilir.

const LOCALE_LABELS: Record<Locale, string> = {
  tr: "🇹🇷 Türkçe",
  en: "🇬🇧 English",
  ar: "🇸🇦 العربية",
};

function LocaleSelect({
  value,
  onChange,
}: {
  value: Locale;
  onChange: (v: Locale) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Locale)}
      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
    >
      {SUPPORTED_LOCALES.map((l) => (
        <option key={l} value={l}>
          {LOCALE_LABELS[l]}
        </option>
      ))}
    </select>
  );
}
